import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { OrganisationTypeConstants } from "../../../../../constants";
import { IStateOrigin } from "../../../../../navigators/navigateStateObjects";
import { logError } from "../../../../../service/error";
import { useAuth } from "../../../../../useAuth";
import {
  getDealListingRoute,
  getLandholdingDashboardRoute,
  getProjectDetailsByUuid,
} from "../../../../../utils/routes";
import { KanbanTask } from "../../../../../widget";
import { TasksDashboardGroup } from "../models";

export interface UseTasksDashboardObjectLinkArgs {
  /** The loaded group headers — the only place the object's type is known. */
  groups: TasksDashboardGroup[];
  /** The loaded cards, which carry their own linked object — the Kanban has no group headers. */
  tasks: KanbanTask[];
}

export interface UseTasksDashboardObjectLinkReturnData {
  /** Navigates to the group's parent object. The uuid alone is enough — the type is looked up here. */
  onGroupObjectClick: (objectUuid: string) => void;
  /** Navigates to a card's linked object, identified by the task the link was clicked on. */
  onObjectClick: (taskId: string) => void;
}

/**
 * The route for one parent object. The grouped views identify an object by uuid alone (a group *is*
 * the object), so the type — which decides the route — is read from the group header rather than
 * carried in the callback.
 */
const getObjectRoute = (objectType: string | undefined, uuid: string, userType: OrganisationTypeConstants): string => {
  switch (objectType?.toLowerCase()) {
    case "project":
      return getProjectDetailsByUuid(uuid, userType);
    case "deal":
      return getDealListingRoute(uuid, userType);
    case "landholding":
      return getLandholdingDashboardRoute(uuid, userType);
    default:
      logError({
        error: `In 'useTasksDashboardObjectLink', a task group has an object type with no route: ${objectType}`,
      });
      return "";
  }
};

/**
 * Navigation for a grouped view's parent-object link (BR-02824's three parent types: Project, Deal,
 * Landholding).
 *
 * A route helper returns "" for an organisation type that has no such page — a Developer has no deal
 * listing, for instance — and logs it itself; navigating to "" would take the user to the root, so an
 * unroutable object leaves them where they are instead.
 */
export const useTasksDashboardObjectLink = ({
  groups,
  tasks,
}: UseTasksDashboardObjectLinkArgs): UseTasksDashboardObjectLinkReturnData => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUserType } = useAuth();

  const objectTypeByUuid = useMemo(
    () => new Map(groups.map((group) => [group.objectUuid, group.objectType ?? undefined])),
    [groups]
  );

  /*
    A card carries its own linked object, so the Kanban needs no group headers to resolve one — which
    is what makes the link work there, since only the grouped views load groups at all.
  */
  const objectByTaskId = useMemo(
    () => new Map(tasks.map((task) => [task.id, { type: task.linkedObjectType, uuid: task.linkedObjectUuid }])),
    [tasks]
  );

  const goToObject = useCallback(
    (objectType: string | undefined, objectUuid: string): void => {
      const route = getObjectRoute(objectType, objectUuid, currentUserType);
      if (!route) return;

      /*
        Carry where the user came from, so the object's own back button returns to the dashboard rather
        than falling through to its default ("Back to projects" on a project, the pipeline tracker on a
        deal). Passed as router state rather than an `origin` query param because all three destinations
        — project, deal and landholding — check `state.origin` first and none of them would need a new
        case adding for the dashboard, where only the project pages read the param.
      */
      navigate(route, {
        state: { origin: { goBackText: "Back to tasks", from: location.pathname } as IStateOrigin },
      });
    },
    [currentUserType, navigate, location.pathname]
  );

  const onGroupObjectClick = useCallback(
    (objectUuid: string): void => {
      // Only the loaded groups are clickable, so a uuid that isn't among them is not a real click.
      if (!objectTypeByUuid.has(objectUuid)) return;

      goToObject(objectTypeByUuid.get(objectUuid), objectUuid);
    },
    [objectTypeByUuid, goToObject]
  );

  const onObjectClick = useCallback(
    (taskId: string): void => {
      const object = objectByTaskId.get(taskId);
      // A task whose parent didn't come back from the search has nothing to navigate to; the card
      // renders the name it does have rather than a link, so this is only reachable in principle.
      if (!object?.uuid) return;

      goToObject(object.type, object.uuid);
    },
    [objectByTaskId, goToObject]
  );

  return { onGroupObjectClick, onObjectClick };
};
